/**
 * Booking CRM helpers.
 *
 * Derives operational views from existing booking, availability,
 * and membership data. No new Firestore collections — everything
 * is computed from existing data.
 *
 * Used by the Organization Dashboard Bookings CRM view.
 */
import { resolveInstructorCategory, isOperationalStaff } from './instructorCategory';
import { isPositivelyUnpaid, normalizePaymentState, PAYMENT } from './paymentSemantics';
import { eligibleForActivity, ACTIVITY } from './activityModel';
import { isClientBooking, isLiveBooking } from './scheduleModel';
import { opsDateKey, opsStartOfDay, opsShiftDays, opsWeekStart, zonedMinutesOfDay, LEGACY_OPS_TIMEZONE } from './opsTime';

// ─────────────────────────────────────────────
// Attention queue — items that need ops action
// ─────────────────────────────────────────────

export function buildAttentionQueue({ bookings = [], bookingRequests = [], members = [] }) {
  const items = [];
  // Pass 61A — attention items are about LIVE work. Retired/superseded bootstrap placeholders and cancelled
  // records (active:false / lifecycleState:'retired_superseded' / rebaselineSuperseded / canceled) must NEVER
  // surface as unpaid / needs-assignment / follow-up. (isLiveBooking keeps completed; it drops retired+cancelled.)
  const liveBookings = bookings.filter(isLiveBooking);

  for (const b of liveBookings) {
    if (isPositivelyUnpaid(b) && b.startAt) {
      items.push({
        id: `unpaid-${b.id}`,
        type: 'unpaid',
        priority: 1,
        label: `${b.serviceNameSnapshot || 'Session'} — ${b.requesterName || 'Client'}`,
        detail: `${getMemberName(members, b.instructorId)} · ${fmtDate(b.startAt)} · ${fmtTime(b.startAt)}`,
        bookingId: b.id,
        timestamp: b.startAt,
      });
    }
  }

  for (const r of bookingRequests) {
    // Pass 50A §10: NEW cases (caseModelVersion=1) use the STAGE axis for attention — an inherited
    // legacy base status='pending' must NOT make a confirmed case look like a pending enquiry.
    const isNewCase = r.caseModelVersion === 1;
    const isPendingEnquiry = isNewCase ? (r.stage === 'inquiry') : (r.status === 'pending');
    if (isPendingEnquiry) {
      items.push({
        id: `pending-req-${r.id}`,
        type: 'pending_request',
        priority: 2,
        label: `${r.requesterName || 'New enquiry'} — ${r.serviceNameSnapshot || 'Lesson'}`,
        detail: r.notes || 'Pending enquiry',
        requestId: r.id,
        timestamp: r.createdAt,
      });
    }
  }

  for (const r of bookingRequests) {
    // §10 the legacy "accepted → needs confirmation" state applies to LEGACY requests only;
    // new cases express confirmation via stage/assignment, not this status.
    if (r.caseModelVersion !== 1 && r.status === 'accepted') {
      items.push({
        id: `unconfirmed-${r.id}`,
        type: 'needs_confirmation',
        priority: 2,
        label: `${r.requesterName || 'Client'} — needs confirmation`,
        detail: `Accepted but not yet confirmed`,
        requestId: r.id,
        timestamp: r.handledAt || r.createdAt,
      });
    }
  }

  for (const b of liveBookings) {
    if (b.status === 'confirmed' && !b.instructorId) {
      items.push({
        id: `no-instructor-${b.id}`,
        type: 'needs_assignment',
        priority: 1,
        label: `${b.requesterName || 'Client'} — no instructor`,
        detail: `${b.serviceNameSnapshot || 'Session'} · ${fmtDate(b.startAt)}`,
        bookingId: b.id,
        timestamp: b.startAt,
      });
    }
  }

  for (const b of liveBookings) {
    if (b.requesterChangeRequest?.status === 'pending') {
      items.push({
        id: `change-req-${b.id}`,
        type: 'change_request',
        priority: 2,
        label: b.requesterChangeRequest.type === 'cancel' ? 'Cancellation request' : 'Reschedule request',
        detail: `${b.requesterName || 'Client'} — ${b.requesterChangeRequest.note || ''}`,
        bookingId: b.id,
        timestamp: b.requesterChangeRequest.requestedAt || b.updatedAt,
      });
    }
  }

  for (const b of liveBookings) {
    if (b.status === 'completed' && b.followUpRecommended && !b.followUpBookingId) {
      items.push({
        id: `followup-${b.id}`,
        type: 'follow_up',
        priority: 3,
        label: `Follow-up — ${b.requesterName || 'Client'}`,
        detail: 'Session completed, no follow-up booked',
        bookingId: b.id,
        timestamp: b.updatedAt,
      });
    }
  }

  return items.sort((a, b) => a.priority - b.priority);
}

// ─────────────────────────────────────────────
// Daily schedule — bookings grouped by instructor
// ─────────────────────────────────────────────

/**
 * Build a daily schedule view showing ALL instructors (not just those with bookings).
 * Groups bookings by instructor, sorted by start time.
 * Includes instructors with no bookings so they show as "available".
 */
export function buildDailySchedule({ bookings = [], members = [], date = new Date(), seasonId = '' }) {
  // §8/§9 resolve category for the SELECTED season when given (never the latest season).
  const catOpts = seasonId ? { season: seasonId } : {};
  // Pass 49B §7: bucket by the canonical Asia/Tokyo calendar day, not the browser's local day.
  // Untimed historical records (null startAt, real serviceDate) match on their serviceDate.
  const dayKey = opsDateKey(date);

  const dayBookings = bookings.filter(b => {
    // Pass 61A — exclude cancelled AND retired/superseded bootstrap placeholders (canonical isLiveBooking),
    // so neither the per-instructor rows nor the derived "__unassigned__" row leak stale demand.
    if (!isLiveBooking(b)) return false;
    const startKey = b.startAt ? opsDateKey(b.startAt) : '';
    if (startKey) return startKey === dayKey;
    return b.serviceDate === dayKey;
  });

  // Group by instructor
  const byInstructor = new Map();
  for (const b of dayBookings) {
    const key = b.instructorId || '__unassigned__';
    if (!byInstructor.has(key)) byInstructor.set(key, []);
    byInstructor.get(key).push(b);
  }

  // Build rows — include ALL instructors, even those with no bookings. Pass 59B3 — exclude the explicit
  // test/dev marker (testAccount:true / rosterSource:'test') so a test member never becomes a production
  // Master Schedule row (consistent with getMembersForSeason).
  const instructors = members.filter((m) => m.testAccount !== true && m.rosterSource !== 'test' && isOperationalStaff(m));
  const rows = [];

  for (const inst of instructors) {
    const bks = byInstructor.get(inst.userId) || [];
    // Pass 46F/§8: canonical category for the selected season. Unclassified must NOT silently
    // borrow School/Collective rates.
    const category = resolveInstructorCategory(inst, catOpts);
    const pool = category === 'collective' ? 'collective' : category === 'school' ? 'internal' : 'unclassified';
    const discipline = inst.labels?.discipline || '';
    const rate = category === 'unclassified'
      ? 'Category required'
      : category === 'collective'
        ? (inst.labels?.dayRate ? `¥${(inst.labels.dayRate / 1000).toFixed(0)}k/day` : '')
        : (inst.labels?.teachingRate ? `¥${inst.labels.teachingRate.toLocaleString()}/hr` : '');

    rows.push({
      instructorId: inst.userId,
      instructorName: inst.displayName || inst.name || inst.userId,
      instructorPool: pool,
      category,
      member: inst, // §9 expose the source member so views can resolve season-aware category
      discipline,
      rate,
      bookings: bks.sort((a, b) => compareTimestamps(a.startAt, b.startAt)),
    });

    byInstructor.delete(inst.userId);
  }

  // Add unassigned if any
  const unassigned = byInstructor.get('__unassigned__');
  if (unassigned?.length) {
    rows.push({
      instructorId: '__unassigned__',
      instructorName: 'Unassigned',
      instructorPool: 'internal',
      discipline: '',
      rate: '',
      bookings: unassigned,
    });
  }

  // Sort: internal first, then collective
  const poolOrder = { internal: 0, collective: 1 };
  rows.sort((a, b) => {
    const po = (poolOrder[a.instructorPool] || 0) - (poolOrder[b.instructorPool] || 0);
    if (po !== 0) return po;
    // Within pool: instructors with bookings first, then alphabetical
    if (a.bookings.length && !b.bookings.length) return -1;
    if (!a.bookings.length && b.bookings.length) return 1;
    return (a.instructorName || '').localeCompare(b.instructorName || '');
  });

  return rows;
}

// ─────────────────────────────────────────────
// Stats strip — summary metrics
// ─────────────────────────────────────────────

export function computeBookingStats({ bookings = [], bookingRequests = [], availabilitySlots = [], members = [], referenceDate = null, seasonId = '' }) {
  // §13 windows are anchored on the SELECTED operational date (JST), not new Date().
  const ref = referenceDate || new Date();
  const catOpts = seasonId ? { season: seasonId } : {};
  const todayStartMs = opsStartOfDay(ref).getTime();
  const nextDayMs = opsShiftDays(ref, 1).getTime();
  // §7 "this week" = the JST calendar week (Mon–Sun) containing the selected date.
  const weekStartMs = opsWeekStart(ref).getTime();
  const weekEndMs = opsShiftDays(opsWeekStart(ref), 7).getTime();
  const inDay = (b) => { const s = toDate(b.startAt); const t = s ? s.getTime() : null; return t != null && t >= todayStartMs && t < nextDayMs; };
  const inWeek = (b) => { const s = toDate(b.startAt); const t = s ? s.getTime() : null; return t != null && t >= weekStartMs && t < weekEndMs; };

  const activeBookings = bookings.filter(isLiveBooking); // Pass 61A — exclude cancelled AND retired/superseded
  const todayBookings = activeBookings.filter(inDay);
  const weekBookings = activeBookings.filter(inWeek);
  const pendingRequests = bookingRequests.filter(r => r.status === 'pending');
  const unpaidBookings = activeBookings.filter(isPositivelyUnpaid); // Pass 47C: positive evidence only
  // §2 lesson CAPACITY = teaching-capable staff for the selected season (current → confirmed-
  // current + teaching; historical → that season's teaching evidence). Drivers/concierge-only
  // staff (Jed) do NOT add lesson slots or dilute lesson utilisation.
  const lessonStaff = eligibleForActivity(members, ACTIVITY.LESSON, seasonId);
  const internalInstructors = lessonStaff.filter(i => resolveInstructorCategory(i, catOpts) === 'school');
  const collectiveInstructors = lessonStaff.filter(i => resolveInstructorCategory(i, catOpts) === 'collective');

  // §1 lesson UTILISATION counts CLIENT LESSON bookings only. A teaching person doing only
  // driving/admin today is operationally busy (Day/Week) but is NOT teaching a lesson, so they
  // do not consume a lesson-utilisation slot.
  const bookedInstructorIds = new Set(todayBookings.filter(isClientBooking).map(b => b.instructorId).filter(Boolean));
  const bookedLessonStaff = lessonStaff.filter(i => bookedInstructorIds.has(i.userId)).length;
  const freeInstructors = lessonStaff.filter(i => !bookedInstructorIds.has(i.userId));

  // Utilization: lesson slots booked / total lesson capacity (each teaching person = 1 slot)
  const totalSlots = lessonStaff.length || 1;
  const utilizationPct = lessonStaff.length > 0
    ? Math.round((bookedLessonStaff / totalSlots) * 100)
    : 0;

  // Pull-in count (collective instructors booked on SAS bookings this week)
  const pullInCount = weekBookings.filter(b =>
    b.assignmentSource === 'collective_pull_in' ||
    (b.previousInstructorId && b.reassignedAt)
  ).length;

  return {
    todayCount: todayBookings.length,
    weekCount: weekBookings.length,
    pendingRequests: pendingRequests.length,
    unpaidCount: unpaidBookings.length,
    totalInstructors: lessonStaff.length,
    internalCount: internalInstructors.length,
    collectiveCount: collectiveInstructors.length,
    freeToday: freeInstructors.length,
    bookedToday: bookedLessonStaff,
    availabilitySlots: availabilitySlots.length,
    utilizationPct,
    pullInCount,
  };
}

// ─────────────────────────────────────────────
// Weekly utilization — per-instructor load
// ─────────────────────────────────────────────

export function computeWeeklyUtilization({ bookings = [], availabilitySlots = [], members = [], referenceDate = null, seasonId = '' }) {
  // §7 the week is the JST CALENDAR week (Mon–Sun) CONTAINING the selected date, not
  // referenceDate..+7 and not new Date().
  const ref = referenceDate || new Date();
  const catOpts = seasonId ? { season: seasonId } : {};
  const weekStart = opsWeekStart(ref);
  const weekStartMs = weekStart.getTime();
  const weekEndMs = opsShiftDays(weekStart, 7).getTime();
  // §8 the 7 JST day-keys of the selected week, for scoping dated availability slots.
  const weekDayKeys = new Set(Array.from({ length: 7 }, (_, i) => opsDateKey(opsShiftDays(weekStart, i))));

  // §2 weekly utilisation is a LESSON-capacity view: teaching staff for the season.
  const instructors = eligibleForActivity(members, ACTIVITY.LESSON, seasonId);
  const activeBookings = bookings.filter(isLiveBooking); // Pass 61A — exclude cancelled AND retired/superseded

  return instructors.map(inst => {
    // §1 the weekly LESSON-utilisation view counts CLIENT LESSON bookings only (non-lesson work
    // shows in Day/Week operational occupancy, not here).
    const weekBks = activeBookings.filter(b => {
      const s = toDate(b.startAt);
      const t = s ? s.getTime() : null;
      return isClientBooking(b) && b.instructorId === inst.userId && t != null && t >= weekStartMs && t < weekEndMs;
    });

    const hours = weekBks.reduce((sum, b) => {
      const dur = b.serviceDurationSnapshot || 60;
      return sum + dur / 60;
    }, 0);

    // §8 count only slots that fall in the selected JST week: a dated (specificDate) slot must
    // be one of this week's days; a recurring (dayOfWeek) slot applies to every week.
    const slots = availabilitySlots.filter(s => s.instructorId === inst.userId && (
      s.specificDate ? weekDayKeys.has(s.specificDate) : (s.dayOfWeek ? true : (s.startAt ? weekDayKeys.has(opsDateKey(s.startAt)) : false))
    ));
    const pct = weekBks.length > 0 ? Math.min(100, Math.round((weekBks.length / 7) * 100)) : 0;

    return {
      instructorId: inst.userId,
      name: inst.displayName || inst.name || inst.userId,
      pool: resolveInstructorCategory(inst, catOpts) === 'collective' ? 'collective' : resolveInstructorCategory(inst, catOpts) === 'school' ? 'internal' : 'unclassified', // Pass 48 §24/§8
      discipline: inst.labels?.discipline || '',
      bookingsThisWeek: weekBks.length,
      hoursThisWeek: Math.round(hours * 10) / 10,
      slotsPublished: slots.length,
      pct,
      load: weekBks.length <= 2 ? 'light' : weekBks.length <= 5 ? 'moderate' : 'heavy',
    };
  }).sort((a, b) => b.bookingsThisWeek - a.bookingsThisWeek);
}

// ─────────────────────────────────────────────
// Shared booking chip colour constants
// ─────────────────────────────────────────────

export const CHIP_COLORS = {
  green:  { bg: '#E1F5EE', c: '#085041' },
  amber:  { bg: '#FEF3C7', c: '#92400E' },
  blue:   { bg: '#EFF4FE', c: '#1A3FA0' },
  red:    { bg: '#FEF2F2', c: '#991B1B' },
  gray:   { bg: '#F4F3F0', c: '#57534E' },
  purple: { bg: '#EDE9FE', c: '#4C1D95', border: '1.5px dashed #C4B5FD' },
};

// ─────────────────────────────────────────────
// Booking chip helpers
// ─────────────────────────────────────────────

export function getBookingChipStatus(booking) {
  // Pass 59B3 — a schedule-grid hold is a PROVISIONAL operational commitment, not a client booking: show a
  // neutral "Hold" chip (never a payment state, never a client identity).
  if (isGridHoldB(booking)) return { label: 'Hold', color: 'purple' };
  if (booking.status === 'canceled') return { label: 'Cancelled', color: 'red' };
  if (booking.status === 'completed') return { label: 'Completed', color: 'green' };
  if (booking.status === 'no_show') return { label: 'No Show', color: 'red' };
  if (booking.status === 'in_progress') return { label: 'In Progress', color: 'blue' };

  if (booking.status === 'confirmed') {
    // §11 route payment meaning through the canonical semantics. Legacy hardcoded 'unpaid'
    // and missing state → "Payment not recorded" (neutral), NOT genuine collectible debt.
    // Only positive not-paid evidence is amber "Not paid".
    const ps = normalizePaymentState(booking.paymentState ?? booking.paymentStatus);
    if (ps === PAYMENT.PAID) return { label: 'Paid', color: 'green' };
    if (ps === PAYMENT.DEPOSIT) return { label: 'Deposit', color: 'blue' };
    if (ps === PAYMENT.NOT_PAID) return { label: 'Not paid', color: 'amber' };
    if (ps === PAYMENT.INQUIRY) return { label: 'Inquiry', color: 'blue' };
    if (ps === PAYMENT.NOT_RECORDED) return { label: 'Payment not recorded', color: 'gray' };
    return { label: 'Confirmed', color: 'blue' };
  }

  if (booking.status === 'pending') return { label: 'Pending', color: 'blue' };
  if (booking.status === 'accepted') return { label: 'Accepted', color: 'blue' };

  return { label: booking.status || 'Unknown', color: 'gray' };
}

// ─────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────

function toDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function compareTimestamps(a, b) {
  const da = toDate(a);
  const db = toDate(b);
  if (!da && !db) return 0;
  if (!da) return 1;
  if (!db) return -1;
  return da.getTime() - db.getTime();
}

function fmtDate(val) {
  const d = toDate(val);
  if (!d) return '';
  // §7 operational display in Asia/Tokyo
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Tokyo' });
}

function getMemberName(members, id) {
  if (!id) return '';
  const m = members.find(m => m.userId === id);
  return m?.displayName || m?.name || '';
}

export function fmtTime(val) {
  const d = toDate(val);
  if (!d) return '';
  // §7 always display the JST wall-clock, regardless of the operator's timezone
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Tokyo' });
}

export function fmtShortDate(val) {
  return fmtDate(val);
}

export function fmtDayHeader(date) {
  const d = toDate(date) || date;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Tokyo' });
}

/**
 * Classify a booking into AM, PM, or FD (full day).
 * FD = serviceDurationSnapshot >= 360 min, or startAt < 13:00 with no endAt constraint.
 * AM = startAt hour < 13
 * PM = startAt hour >= 13
 */
// Pass 59B3 — a schedule-grid hold (blocked, activityType client_hold) carries no start time; classify it by
// its normalized operationalSlot so a full-day hold occupies the full-day cell (not defaulted to AM).
const isGridHoldB = (b = {}) => b.externalSource === 'sas_schedule_grid' || b.activityType === 'client_hold';
export function classifyBookingPeriod(booking, { timeZone = LEGACY_OPS_TIMEZONE } = {}) {
  if (isGridHoldB(booking)) {
    const s = booking.operationalSlot;
    if (s === 'FULL_DAY' || booking.allDay) return 'fd';
    if (s === 'PM' || s === 'NIGHT') return 'pm';
    return 'am';
  }
  const dur = booking.serviceDurationSnapshot;
  if (dur && dur >= 360) return 'fd';

  const start = toDate(booking.startAt);
  if (!start) return 'am';
  // Pass 83B.2 — classify by the wall-clock hour in the SUPPLIED operating zone (SAS → Asia/Tokyo via the
  // default), not the operator's browser hour and no longer hardcoded JST. Grid-hold/full-day rules above
  // are unaffected by the zone.
  return zonedMinutesOfDay(start, timeZone || LEGACY_OPS_TIMEZONE) < 13 * 60 ? 'am' : 'pm';
}

/**
 * Returns an "Early HH:MM" or "Late HH:MM" badge string if the booking
 * starts outside the standard session window.
 *   AM: 09:00–12:00   PM: 13:00–16:00
 * Full-day bookings are never flagged.
 */
export function getTimeWindowBadge(booking, period, { timeZone = LEGACY_OPS_TIMEZONE } = {}) {
  if (period === 'fd') return null;
  const start = toDate(booking.startAt);
  if (!start) return null;
  // Pass 83B.2 — window checks against the wall-clock in the supplied operating zone (default Asia/Tokyo).
  const mins = zonedMinutesOfDay(start, timeZone || LEGACY_OPS_TIMEZONE);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  if (period === 'am' && h < 9) return `Early ${timeStr}`;
  if (period === 'am' && (h > 12 || (h === 12 && m > 0))) return `Late ${timeStr}`;
  if (period === 'pm' && h < 13) return `Early ${timeStr}`;
  if (period === 'pm' && h >= 16) return `Late ${timeStr}`;
  return null;
}
